<template>
  <div>
    <!-- GLOBAL USERS DIALOG -->
    <el-dialog
      title="Global Users"
      :visible.sync="showDialog"
      :width="globalUsersDialogWidth"
      append-to-body
      @open="fetchGlobalUsers">
      <div class="mb-2">
        <el-input placeholder="Filter by email" v-model="globalUserFilter" clearable></el-input>
      </div>
      <div class="flex flex-col gap-2 h-[500px] overflow-y-auto space-y" v-loading="loadingGlobalUsers">
        <div v-for="user in filteredGlobalUsers" :key="user.id" class=" flex flex-row justify-between items-start bg-black/5 p-2 px-4 mr-2 rounded-sm">
            <div class="flex flex-col grow">
                <div class="font-bold text-black dark:text-white">{{ user.email }}
                   <span class="text-sm text-gray-400 mb-2">( {{ user.name }} )</span>
                </div>
                <div class="mt-1">
                    <div class="flex flex-wrap gap-2">
                        <div v-for="(tag, i) in getUserTags(user)" :key="i">
                          <el-tooltip placement="top">
                             <div slot="content">
                                <strong>Map claimsCanAccess:</strong><br/>
                                <div v-if="tag.map && tag.map.claimsCanAccess && tag.map.claimsCanAccess.length" class="max-h-32 overflow-y-auto">
                                    <div v-for="(claim, ix) in tag.map.claimsCanAccess" :key="'acc-'+ix">- {{ claim }}</div>
                                </div>
                                <div v-else>None</div>
                                <br/>
                                <strong>Map claimsCanEdit:</strong><br/>
                                <div v-if="tag.map && tag.map.claimsCanEdit && tag.map.claimsCanEdit.length" class="max-h-32 overflow-y-auto">
                                    <div v-for="(claim, ix) in tag.map.claimsCanEdit" :key="'edit-'+ix">- {{ claim }}</div>
                                </div>
                                <div v-else>None</div>
                             </div>
                             <span 
                                v-if="!tag.labelClient"
                                :style="{ backgroundColor: tag.color }"
                                :class="'bg-[${tag.color}]'"
                                class="text-black text-[10px] px-2 py-0.5 h-7 rounded border border-white/20 font-medium flex items-center gap-1 cursor-default">
                                {{ tag.label }}
                                <i class="el-icon-close cursor-pointer hover:bg-black/10 rounded-full p-[1px]" @click.stop="unassignUserTag(tag)"></i>
                            </span>
                            <span v-else
                                :style="{ backgroundColor: tag.color }"
                                :class="'bg-[${tag.color}]'"
                                class="text-black text-[10px] px-2 py-0.5 rounded border border-white/20 font-medium flex items-center gap-1 cursor-default">
                                <span >{{ tag.labelClient }}</span>
                                <span class="bg-white/20 relative top-0 inline-flex items-center ml-1.5 -mr-1.5 h-[22px] px-1.5 py-0">
                                    {{ tag.labelAccount }}
                                    <i class="el-icon-close ml-1 cursor-pointer hover:bg-black/10 rounded-full " @click.stop="unassignUserTag(tag)"></i>
                                </span>
                            </span>
                          </el-tooltip>
                        </div>
                    </div>
                </div>
            </div>
            <div class="shrink-0 ml-4 self-center flex items-center">
                 <el-tooltip v-if="getDuplicateWarning(user)" placement="top">
                    <div slot="content">
                        <strong>Duplicate assignments detected:</strong><br/>
                        <div v-for="(line, ix) in getDuplicateWarning(user)" :key="ix">- {{ line }}</div>
                    </div>
                    <i class="el-icon-warning text-[#e6a23c] text-lg mr-3 cursor-pointer"></i>
                 </el-tooltip>
                 <el-tooltip placement="left">
                    <div slot="content">
                        <strong>claimsCanAccess:</strong><br/>
                        <div v-if="user.claimsCanAccess && user.claimsCanAccess.length" class="max-h-32 overflow-y-auto">
                            <div v-for="(claim, ix) in user.claimsCanAccess" :key="'acc-'+ix">- {{ claim }}</div>
                        </div>
                        <div v-else>None</div>
                        <br/>
                        <strong>claimsCanEdit:</strong><br/>
                        <div v-if="user.claimsCanEdit && user.claimsCanEdit.length" class="max-h-32 overflow-y-auto">
                            <div v-for="(claim, ix) in user.claimsCanEdit" :key="'edit-'+ix">- {{ claim }}</div>
                        </div>
                        <div v-else>None</div>
                    </div>
                    <i class="el-icon-info text-gray-400 text-lg mr-3 cursor-pointer"></i>
                 </el-tooltip>
                 <button type="button" class="bg-brand text-black! px-4 py-1 hover:bg-black hover:text-white! rounded-xs cursor-pointer transition" @click="openAssignClientDialog(user)">Assign</button>
            </div>
        </div>
      </div>
    </el-dialog>

    <!-- ASSIGN CLIENT DIALOG -->
    <el-dialog
      title="Assign to Client"
      :visible.sync="assignClientDialog"
      width="30%"
      append-to-body>
      <div v-if="selectedUser">
        <p>Select Client to assign <strong>{{selectedUser.name}}</strong> to:</p>
        <div style="margin-top: 15px;">
          <el-select v-model="selectedClientId" placeholder="Select Client" filterable style="width: 100%;" @change="selectedAccountId = ''">
              <el-option
                v-for="(item, id) in clients"
                :key="id"
                :label="item.name"
                :value="item.id">
              </el-option>
          </el-select>
        </div>
        <div style="margin-top: 15px;" v-if="selectedClientId">
            <p>Select Account (optional):</p>
            <el-select v-model="selectedAccountId" placeholder="Select Account" filterable style="width: 100%;">
                <el-option
                    v-for="account in availableAccounts"
                    :key="account.id"
                    :label="account.name"
                    :value="account.id">
                </el-option>
            </el-select>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="assignClientDialog = false">Cancel</el-button>
        <el-button type="primary" @click="assignUser">Assign</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script setup>
import {
  ref, computed,
} from 'vue';
import { MessageBox, Message } from 'element-ui';
import { generateClient } from 'aws-amplify/api';
import { isMobileOnly } from 'mobile-device-detect';
import ColorGenerator from '../../services/ColorGenerator';
import { getUser, getUserClientMap } from '../../graphql/queries';
import { updateUserClientMap } from '../../graphql/mutations';
import store from '../../store';

const client = generateClient();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible']);

const showDialog = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});

// Data
const globalUsers = ref([]);
const globalUserFilter = ref('');
const loadingGlobalUsers = ref(false);
const assignClientDialog = ref(false);
const selectedUser = ref(null);
const selectedClientId = ref('');
const selectedAccountId = ref('');

// Computed
const userId = computed(() => store.state.userAccount.userId);
const clients = computed(() => store.state.admin.clients);
const globalUsersDialogWidth = computed(() => (isMobileOnly ? '95%' : '70%'));

const filteredGlobalUsers = computed(() => {
  if (!globalUserFilter.value) {
    return globalUsers.value;
  }
  return globalUsers.value.filter((u) => u.email.toLowerCase().includes(globalUserFilter.value.toLowerCase()));
});

const availableAccounts = computed(() => {
  if (!selectedClientId.value) return [];
  const clientItem = clients.value[selectedClientId.value];
  if (!clientItem || !clientItem.accounts || !clientItem.accounts.items) return [];
  return clientItem.accounts.items;
});

// Methods
const fetchGlobalUsers = async () => {
  loadingGlobalUsers.value = true;
  const result = await store.dispatch('admin/fetchGlobalUserList');
  if (result && result.items) {
    globalUsers.value = result.items;
  }
  loadingGlobalUsers.value = false;
};

const getUserTags = (user) => {
  const tags = [];
  const addedLabels = new Set();
  const uId = user.id;

  // 1. Add direct client assignments
  if (user.clients && user.clients.items) {
    user.clients.items.forEach((map) => {
      if (map && map.client) {
        const label = map.client.name;
        if (!addedLabels.has(label)) {
          tags.push({
            label,
            color: ColorGenerator.generateNewColor(label),
            type: 'client',
            mapId: map.id,
            userId: uId,
            clientId: map.client.id,
            map: map,
          });
          addedLabels.add(label);
        }
      }
    });
  }

  // 2. Add Account assignments
  if (user.accounts && user.accounts.items) {
    user.accounts.items.forEach((map) => {
      if (map && map.accounts) {
        const account = map.accounts;
        const clientName = (account.client && account.client.name) ? account.client.name : 'Unknown';
        const label = `${clientName} | ${account.name}`;

        // Color by client name
        const color = ColorGenerator.generateNewColor(clientName);

        if (!addedLabels.has(label)) {
          tags.push({
            label,
            labelClient: clientName,
            labelAccount: account.name,
            color,
            type: 'account',
            mapId: map.id,
            userId: uId,
            accountId: account.id,
            clientId: account.client ? account.client.id : null,
            map: map,
          });
          addedLabels.add(label);
        }
      }
    });
  }
  return tags.sort((a, b) => a.label.localeCompare(b.label));
};

const unassignUserTag = async (tag) => {
  if (!tag.mapId) return;

  try {
    const action = tag.type === 'client' ? 'admin/unassignUserFromClient' : 'admin/unassignUserFromAccount';
    const message = `Are you sure you want to unassign user from <strong>${tag.label}</strong>?`;

    await MessageBox.confirm(message, 'Warning', {
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      dangerouslyUseHTMLString: true,
      type: 'warning',
    });

    const result = await store.dispatch(action, tag.mapId);

    if (result && !result.error) {
      // Update Claims
      try {
        const { data } = await client.graphql({
          query: getUser,
          variables: { id: tag.userId },
        });
        const freshUser = data.getUser;
        if (freshUser) {
          let nextAccess = [...(freshUser.claimsCanAccess || [])];
          let nextEdit = [...(freshUser.claimsCanEdit || [])];

          if (tag.type === 'client') {
            // Remove usr.{clientId}
            if (tag.clientId) {
              const claimToRemove = `usr.${tag.clientId}`;
              nextAccess = nextAccess.filter((c) => c !== claimToRemove);
            }
          } else if (tag.type === 'account') {
            // Remove aa.{accountId}.{clientId} and ca.{accountId}.{clientId}
            if (tag.accountId && tag.clientId) {
              const claimsToRemove = [
                `aa.${tag.accountId}.${tag.clientId}`,
                `ca.${tag.accountId}.${tag.clientId}`,
              ];
              nextEdit = nextEdit.filter((c) => !claimsToRemove.includes(c));
            }
          }

          if (nextAccess.length !== (freshUser.claimsCanAccess || []).length
                  || nextEdit.length !== (freshUser.claimsCanEdit || []).length) {
            await store.dispatch('admin/updateUser', {
              id: freshUser.id,
              expectedVersion: freshUser.version,
              claimsCanAccess: nextAccess,
              claimsCanEdit: nextEdit,
            });
          }
        }
      } catch (err) {
        console.error('Failed to update user claims after unassign', err);
      }

      await fetchGlobalUsers();

      // Check and update UserClientMap claims if needed
      if (tag.type === 'account' && tag.clientId) {
        const updatedUser = globalUsers.value.find((u) => u.id === tag.userId);
        if (updatedUser) {
          // Find the UserClientMap for this client
          const clientMap = updatedUser.clients?.items?.find(
            (item) => item && item.client && item.client.id === tag.clientId,
          );

          if (clientMap) {
            // Find all remaining accounts for this client
            const remainingAccounts = updatedUser.accounts?.items?.filter(
              (item) => item && item.accounts && item.accounts.client && item.accounts.client.id === tag.clientId,
            );

            // Construct expected claims
            const expectedClaims = [];
            if (remainingAccounts) {
              remainingAccounts.forEach((item) => {
                const accId = item.accounts.id;
                const cliId = tag.clientId;
                expectedClaims.push(`aa.${accId}.${cliId}`);
                expectedClaims.push(`ca.${accId}.${cliId}`);
              });
            }

            const currentClaims = clientMap.claimsCanEdit || [];

            // Simple array equality check (ignoring order)
            const isDifferent = expectedClaims.length !== currentClaims.length
                                || !expectedClaims.every((c) => currentClaims.includes(c));

            if (isDifferent) {
              try {
                // Fetch fresh version of client map to ensure update succeeds
                const { data: mapData } = await client.graphql({
                  query: getUserClientMap,
                  variables: { id: clientMap.id },
                });

                if (mapData && mapData.getUserClientMap) {
                  const freshMap = mapData.getUserClientMap;
                  await client.graphql({
                    query: updateUserClientMap,
                    variables: {
                      input: {
                        id: freshMap.id,
                        claimsCanEdit: expectedClaims,
                        _version: freshMap.version,
                      },
                    },
                  });
                  // Refresh UI again to reflect updated claims in tooltips
                  await fetchGlobalUsers();
                }
              } catch (e) {
                console.error('Failed to correct UserClientMap claims', e);
              }
            }
          }
        }
      }
    }
  } catch (err) {
    console.log('Unassign cancelled or failed', err);
  }
};

const getDuplicateWarning = (user) => {
  const duplicates = [];

  // Clients
  if (user.clients?.items) {
    const counts = {};
    const names = {};
    user.clients.items.filter(i => i && i.client).forEach(item => {
        counts[item.client.id] = (counts[item.client.id] || 0) + 1;
        names[item.client.id] = item.client.name;
    });
    
    Object.keys(counts).forEach(id => {
      if (counts[id] > 1) {
        duplicates.push(`Client: ${names[id]} (${counts[id]})`);
      }
    });
  }

  // Accounts
  if (user.accounts?.items) {
    const counts = {};
    const names = {};
    user.accounts.items.filter(i => i && i.accounts).forEach(item => {
        const accId = item.accounts.id;
        counts[accId] = (counts[accId] || 0) + 1;
        names[accId] = item.accounts.name;
    });

    Object.keys(counts).forEach(id => {
      if (counts[id] > 1) {
        duplicates.push(`Account: ${names[id]} (${counts[id]})`);
      }
    });
  }

  return duplicates.length ? duplicates : null;
};

const openAssignClientDialog = (userRow) => {
  selectedUser.value = userRow;
  selectedClientId.value = '';
  selectedAccountId.value = '';
  assignClientDialog.value = true;
};

const assignUser = async () => {
  if (!selectedClientId.value || !selectedUser.value) return;

  const promises = [];
  const user = selectedUser.value;

  // Determine account ID first
  let accountToAssign = selectedAccountId.value;
  if (!accountToAssign && availableAccounts.value.length > 0) {
     // If no account selected, select the first one (usually default)
     accountToAssign = availableAccounts.value[0].id;
  }

  // Calculate claims for Client Map (aa.ACC.CLI + ca.ACC.CLI for all assigned accounts)
  const existingAccountIds = user.accounts?.items
    ?.filter((m) => m.accounts?.client?.id === selectedClientId.value)
    .map((m) => m.accounts.id) || [];

  const allAccountIds = new Set(existingAccountIds);
  if (accountToAssign) {
    allAccountIds.add(accountToAssign);
  }

  const clientMapClaimsCanEdit = [];
  allAccountIds.forEach((accId) => {
    clientMapClaimsCanEdit.push(`aa.${accId}.${selectedClientId.value}`);
    clientMapClaimsCanEdit.push(`ca.${accId}.${selectedClientId.value}`);
  });

  // Check if client is already assigned
  const existingClientMap = user.clients?.items?.find(
    (item) => item.client?.id === selectedClientId.value,
  );

  if (!existingClientMap) {
    promises.push(store.dispatch('admin/assignUserToClient', {
      userClientMapUserId: user.id,
      userClientMapClientId: selectedClientId.value,
      userClientMapLastUpdatedById: userId.value,
      claimsCanAccess: [`user.${user.id}`, `usr.${selectedClientId.value}`],
      claimsCanEdit: clientMapClaimsCanEdit,
    }));
  } else {
    // Update existing client map
    const input = {
      id: existingClientMap.id,
      claimsCanEdit: clientMapClaimsCanEdit,
    };
    if (existingClientMap._version) {
      input._version = existingClientMap._version;
    }
    promises.push(client.graphql({
      query: updateUserClientMap,
      variables: { input },
    }));
  }

  if (accountToAssign) {
      // Check if account is already assigned
      const accountAlreadyAssigned = user.accounts?.items?.some(
        (item) => item.accounts?.id === accountToAssign,
      );

      if (!accountAlreadyAssigned) {
        promises.push(store.dispatch('admin/assignUserToAccount', {
            userAccountMapUserId: user.id,
            userAccountMapAccountsId: accountToAssign,
            userAccountMapLastUpdatedById: userId.value,
            claimsCanAccess: [`user.${user.id}`, `usr.${selectedClientId.value}`],
            claimsCanEdit: [`aa.${accountToAssign}.${selectedClientId.value}`, `ca.${accountToAssign}.${selectedClientId.value}`],
        }));

        // Update User Claims
        promises.push((async () => {
             try {
                const { data } = await client.graphql({
                    query: getUser,
                    variables: { id: user.id },
                });
                const freshUser = data.getUser;
                if (freshUser) {
                    const nextAccess = [...new Set([
                        ...(freshUser.claimsCanAccess || []),
                        `user.${freshUser.id}`,
                        `usr.${selectedClientId.value}`
                    ])];
                    const nextEdit = [...new Set([
                        ...(freshUser.claimsCanEdit || []),
                        `aa.${accountToAssign}.${selectedClientId.value}`,
                        `ca.${accountToAssign}.${selectedClientId.value}`
                    ])];

                    await store.dispatch('admin/updateUser', {
                        id: freshUser.id,
                        expectedVersion: freshUser.version,
                        claimsCanAccess: nextAccess,
                        claimsCanEdit: nextEdit,
                    });
                }
             } catch (err) {
                 console.error('Failed to update user claims', err);
             }
        })());

      } else {
         if (existingClientMap) {
             // Only show message if user was already assigned to client AND account
             Message({
                message: 'User already assigned to this account',
                type: 'warning',
             });
         }
      }
  }

  if (promises.length > 0) {
    await Promise.all(promises);
    assignClientDialog.value = false;
    fetchGlobalUsers();
  }
};
</script>
